import useAuthStore from "../stores/useAuthStore";
import useReferenceStore from "../stores/useReferenceStore";
import useTransactionStore from "../stores/useTransactionStore";

/**
 * Determine if a transaction is inter-state based on customer address vs store location.
 * Extracted from useOrderProcessing.getIsInterState for reuse in printers and tax breakup.
 *
 * @param {Object} cart - Cart/order object with customer.metaData
 * @returns {boolean}
 */
export function getIsInterState(cart) {
  try {
    const tillData = useAuthStore.getState().tillData;
    const storeStateId = tillData?.tillAccess?.csBunit?.csRegionId || "";
    if (!storeStateId) return false;

    // Read B2B flag from both sources — createCustomer sets only the store,
    // editOldCustomer sets both. Checking both ensures correct group name in all flows.
    const storedB2B =
      localStorage.getItem("isB2bEnabled") === "true" ||
      useTransactionStore.getState().isB2bEnabled === "true";
    const groupName = storedB2B ? "Delivery Address" : "Address";

    let customerStateId = "";

    // Primary: resolve state from the customer config address group in metaData
    const customerConfigs = useReferenceStore.getState().customerConfigs;
    const customerConfigData = customerConfigs?.[0]?.configuration
      ? JSON.parse(customerConfigs[0].configuration)
      : {};
    const fieldGroups = customerConfigData?.fieldGroups || [];
    const groupId = fieldGroups.find((g) => g.name === groupName)?.id;

    if (groupId) {
      const metaRow = cart?.customer?.metaData?.find((item) => item.key === groupId);
      if (metaRow?.value) {
        const parsed =
          typeof metaRow.value === "string" ? JSON.parse(metaRow.value) : metaRow.value;
        customerStateId = parsed?.state || "";

        // Resolve name → RecordID if needed
        if (customerStateId) {
          const statesData = useReferenceStore.getState().statesData;
          const isId = statesData.some((s) => s.RecordID === customerStateId);
          if (!isId) {
            const match = statesData.find(
              (s) => s.Name?.toLowerCase() === customerStateId.toLowerCase()
            );
            customerStateId = match?.RecordID || "";
          }
        }
      }
    } else {
    }

    // Fallback for B2C: if the metaData group did not yield a state, use the
    // customerAddress.csRegionId that is explicitly saved on the customer record.
    // This covers customers without a group-based address in metaData and also
    // guards against the backend not echoing the metaData group back.
    if (!customerStateId && !storedB2B) {
      const fallbackId = cart?.customer?.customerAddress?.csRegionId || "";
      if (fallbackId) {
      }
      customerStateId = fallbackId;
    }

    // Fallback for B2B: search-selecting an existing B2B customer strips the Business Tab's
    // metaData (BUG-1202, on customer.js's customerSearchResults), so the primary lookup
    // above finds no "Delivery Address" group entry. customer.js captures that group's state
    // into b2bDeliveryStateId before stripping specifically for this case. Safe unconditional
    // fallback -- unlike customerAddress.csRegionId, this is never silently defaulted to the
    // store's own region, so it can't produce a false same-store match.
    if (!customerStateId && storedB2B) {
      let fallbackId = cart?.customer?.b2bDeliveryStateId || "";
      if (fallbackId) {
        const statesData = useReferenceStore.getState().statesData;
        const isId = statesData.some((s) => s.RecordID === fallbackId);
        if (!isId) {
          const match = statesData.find((s) => s.Name?.toLowerCase() === fallbackId.toLowerCase());
          fallbackId = match?.RecordID || "";
        }
      }
      customerStateId = fallbackId;
    }

    return Boolean(customerStateId && storeStateId !== customerStateId);
  } catch {
    return false;
  }
}
